import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Chart, { ChartDataset, FontSpec } from 'chart.js/auto';
import { ChargerType, PortalRoutePath } from '@models';
import { CUSTOM_THEME, NumberUtils, Tr, TypeUtils } from '@utils';
import { SimulationInfrastructure } from '@services';
import { Box, Stack } from '@mui/material';
import { DarkChartLegends, ILegendDefinition, LightCardWithTitle } from '@components/common';

const COLOR_MAP: Record<ChargerType, string> = {
	[ChargerType.AC]: CUSTOM_THEME.palette.primary.main,
	[ChargerType.DC]: CUSTOM_THEME.extraColors.pastelRed,
	[ChargerType.CPO]: CUSTOM_THEME.extraColors.mintGreen
};

const getCommonBarChartConfig = (type: ChargerType): ChartDataset<'bar', number[]> => ({
	data: [],
	type: 'bar',
	borderRadius: { bottomLeft: 6, bottomRight: 6, topLeft: 6, topRight: 6 },
	borderSkipped: false,
	maxBarThickness: 24,
	backgroundColor: COLOR_MAP[type] || CUSTOM_THEME.palette.grey[400]
});

const getCommonFontConfig = (): Partial<FontSpec> => ({
	family: 'Montserrat',
	size: 16,
	lineHeight: 1.44
});

export interface IChartInfrastructureProps {
	infrastructure?: SimulationInfrastructure;
}

export const ChartInfrastructure: FC<IChartInfrastructureProps> = ({ infrastructure }) => {
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const chartRef = useRef<HTMLCanvasElement>(null);
	const [totalInfrastructureCost, setTotalInfrastructureCost] = useState(0);
	const [legends, setLegends] = useState<ILegendDefinition[]>([]);
	const [chartInstance, setChartInstance] = useState<Chart>();

	useEffect(() => {
		if (infrastructure && Array.isArray(infrastructure.siteInfrastructure)) {
			setTotalInfrastructureCost(
				(infrastructure.siteInfrastructure || []).reduce((total, current) => total + (current?.infrastructureCost || 0), 0)
			);
		}
		if (chartRef.current && infrastructure && Array.isArray(infrastructure.siteInfrastructure)) {
			chartInstance?.destroy();
			const labels = infrastructure.siteInfrastructure.map(({ site }) => site.name);
			const siteChargerTypeDict = infrastructure.siteInfrastructure.reduce((siteDict, { site, parkingAreaInfrastructure: infra }) => {
				siteDict[site.name] = {
					[ChargerType.AC]: infra.reduce(
						(acc, { acInstalled, acToBeInstalled, voltaageRecommendationACChargers }) =>
							acc + (acToBeInstalled || voltaageRecommendationACChargers || acInstalled || 0),
						0
					),
					[ChargerType.DC]: infra.reduce(
						(acc, { dcInstalled, dcToBeInstalled }) => acc + (dcToBeInstalled || dcInstalled || 0),
						0
					),
					[ChargerType.CPO]: infra.reduce((acc, { cpoToBeInstalled }) => acc + (cpoToBeInstalled || 0), 0)
				};
				return siteDict;
			}, {} as Record<string, Record<ChargerType, number>>);

			const datasets = Object.values(ChargerType).map((chargerType) => {
				return {
					...getCommonBarChartConfig(chargerType),
					label: chargerType,
					data: infrastructure.siteInfrastructure.map(({ site: { name } }) => siteChargerTypeDict[name][chargerType] || 0)
				};
			});
			setLegends(Object.values(ChargerType).map((type) => ({ title: `${type} Charger`, color: COLOR_MAP[type] })));

			setChartInstance(
				new Chart(chartRef.current, {
					type: 'bar',
					data: { datasets, labels },
					options: {
						indexAxis: 'y',
						layout: {
							padding: { top: 32, bottom: 8 }
						},
						scales: {
							x: {
								stacked: true,
								ticks: { font: getCommonFontConfig() },
								beginAtZero: true
							},
							y: {
								stacked: true,
								ticks: { font: getCommonFontConfig() },
								beginAtZero: true
							}
						},
						plugins: {
							legend: { display: false },
							tooltip: {
								intersect: true,
								mode: 'nearest',
								position: 'nearest',
								backgroundColor: '#130c45',
								displayColors: false,
								caretSize: 8,
								caretPadding: 16,
								padding: {
									top: 8,
									bottom: 8,
									right: 24,
									left: 24
								},
								bodyFont: getCommonFontConfig(),
								callbacks: {
									title: () => '',
									label: ({ raw: count, label: address, dataset: { label: type = '' } }) => {
										return [
											`${t('dashboard.executive-summary-infrastructure.address')}: ${address}`,
											`${t('dashboard.executive-summary-infrastructure.charger-type')}: ${type}`,
											`${t('dashboard.executive-summary-infrastructure.count')}: ${count}`
										];
									}
								}
							}
						},
						onClick: (_event, elements) => {
							const index = elements?.[0]?.index;
							if (TypeUtils.isNumber(index)) {
								const site = infrastructure.siteInfrastructure[index]?.site;
								if (site) {
									navigate(
										`/portal/${PortalRoutePath.LOCATION}/${site.id}/infrastructure/${infrastructure.simulationId}`
									);
								}
							}
						},
						onHover: (event, elements) => {
							const target = event?.native?.target as HTMLCanvasElement;
							target?.style && (target.style.cursor = elements[0] ? 'pointer' : 'default');
						}
					}
				})
			);
		}
	}, [chartRef, infrastructure]);

	return (
		<LightCardWithTitle
			headerVariant="dark"
			title={<Tr.Portal path="dashboard.executive-summary-infrastructure.infrastructure-card-title" />}
			actionButton={
				<Box
					sx={{
						paddingY: 0.5,
						paddingX: 1,
						borderRadius: 1.5,
						borderStyle: 'solid',
						borderWidth: 1,
						borderColor: 'common.white',
						color: 'common.white',
						backgroundColor: 'rgba(255, 255, 255, 0.2)',
						lineHeight: 'normal'
					}}
				>
					<Tr.Portal
						path="dashboard.executive-summary-infrastructure.total-cost"
						// TODO: this does not work: it seems to show always 0 and this should never happen (unless vehicles are not linked to a location/parking)
						options={{ price: NumberUtils.formatAsPrice(totalInfrastructureCost, undefined, '0,0') }}
					/>
				</Box>
			}
		>
			<Stack direction="column" alignItems="center" spacing={3}>
				<canvas ref={chartRef} />
				<DarkChartLegends sx={{ minWidth: 500 }} legends={legends} />
			</Stack>
		</LightCardWithTitle>
	);
};
